//
// RecordData
//
// A plain RecordData object to hold properties
//
// We use a gid as a globally unique id, so where we have two records with the same id
// and a different language, we will have two gid's.
//
// The concept of a headingrecord is removed. This concept is replaced by the definition,
// class, and field configuration. When exporting to Stentor or CataloggerClassic, these
// records can be added back in from the new definitions (also: fieldselections).
//
// I know Record is a very generic word, but I mean the Unicat catalog_tree data records.
// These are not only Products but also Chapters, Brands, Categories. If anyone comes
// up with a better word that is non-generic or at least less generic, let me know.

import { action, computed, makeObservable, observable } from 'mobx'

import { gid } from '../../utils/gid'
import { hashData, objectFrom, test_empty } from '../../utils/utils'
import { updateInstanceWithData } from './utils'

export class RecordData {
    gid = null
    canonical = 0
    parent = 0
    backlinks = []
    treelevel = 0
    path = []
    title = new Map()
    status = ''
    channels = []
    orderings = new Map()
    childcount = 0
    definition = null
    fieldselection = []
    fields = new Map()
    version_gid = null
    created_on = 0.0
    updated_on = 0.0

    _modified_fields = new Map()

    constructor(recorddata, rootstore) {
        this.version_gid = gid()

        makeObservable(this, {
            canonical: observable,
            parent: observable,
            backlinks: observable,
            treelevel: observable,
            path: observable,
            title: observable,
            status: observable,
            channels: observable,
            orderings: observable,
            childcount: observable,
            definition: observable,
            fieldselection: observable,
            fields: observable,
            version_gid: observable,
            created_on: observable,
            updated_on: observable,

            title_field: computed,
            image_field: computed,
            is_link: computed,
            data_hash: computed,
            localized_title: computed,
            localized_fields: computed,

            language: computed,

            update: action.bound,
            setField: action.bound,
            _setField: action.bound,
            commitIfModified: action.bound,
            setChannels: action.bound,
            copyChannelsFromParent: action.bound,
            copyChannelsDown: action.bound,
            copyChannelsUp: action.bound,
            delete: action.bound,
            undelete: action.bound,
            unlink: action.bound,

            addDefinitionField: action.bound,
            moveDefinitionField: action.bound,
            removeDefinitionField: action.bound,

            addDefinitionClass: action.bound,
            moveDefinitionClass: action.bound,
            removeDefinitionClass: action.bound,

            addDefinitionFieldlistField: action.bound,
            moveDefinitionFieldlistField: action.bound,
            removeDefinitionFieldlistField: action.bound,

            setDefinition: action.bound,
            revertDefinition: action.bound,
            copyDefinitionToSiblings: action.bound,
            cloneExtendedDefinition: action.bound,
            updateExtendedDefinition: action.bound,
            transferExtendedDefinitionTo: action.bound,
            transferExtendedDefinitionFrom: action.bound,

            bump_version_gid: action.bound,
        })

        this._rootstore = rootstore
        this.update(recorddata)
    }

    bump_version_gid = () => {
        this.version_gid = gid()
    }

    update = recorddata => {
        updateInstanceWithData(
            this,
            recorddata,
            [
                'gid',
                'canonical',
                'parent',
                'backlinks',
                'treelevel',
                'path',
                'title',
                'status',
                'channels',
                'orderings',
                'childcount',
                'definition',
                'fieldselection',
                'created_on',
                'updated_on',
            ],
            ['fields']
        )
        this.bump_version_gid()
    }

    get localized_title() {
        const data = this._rootstore.data
        const definition = data.definitions.get(this.definition)
        if (definition) {
            const field = data.fields.get(definition.titlefield)
            if (
                field &&
                [
                    'text',
                    'textline',
                    'textlist',
                    'number',
                    'decimal',
                    'code',
                    'barcode',
                ].includes(field.type) &&
                this.localized_fields &&
                this.localized_fields.has(field.name)
            ) {
                return this.localized_fields.get(field.name)
            }
        }
        return this.title.get(this._rootstore.view.environment.get('language'))
    }

    get localized_fields() {
        return this.fields.get(this._rootstore.view.environment.get('language'))
    }

    get title_field() {
        const data = this._rootstore.data
        let definition = data.definitions.get(this.definition)
        if (!definition) return undefined
        return data.fields.get(definition.titlefield)
    }

    get image_field() {
        const data = this._rootstore.data
        const definition = data.definitions.get(this.definition)
        if (!definition) return undefined
        const imagefield = data.getFieldByName('image')
        const all_fields = [
            ...definition.fields,
            ...definition.classes
                .map(classgid => {
                    const class_ = data.classes.get(classgid)
                    return class_.fields
                })
                .reduce((result, fields) => [...result, ...fields], []),
        ]
        if (imagefield && all_fields.includes(imagefield.gid)) {
            return imagefield
        }
        for (const fieldgid of all_fields) {
            let field = data.fields.get(fieldgid)
            if (field && field.type === 'image') {
                return field
            }
        }
        return undefined
    }

    get is_link() {
        return this.gid !== this.canonical
    }

    get data_hash() {
        return hashData(this.fields)
    }

    get language() {
        return this._rootstore.view.environment.get('language')
    }

    setClassField = (key, value, recordfield) => {
        // find the right part of the data in a class/classlist field
        // from the key (<recordgid>.<fieldname>[index].<fieldname>....etc)
        let keys = key.split('.')
        if (keys.shift() !== this.gid) {
            console.log('RecordData::setClassField RECORD GID ERROR')
        }
        keys.shift() // chop version_gid
        let keyfound = false
        let datapointer = null
        const lastpartindex = keys.length - 1
        keys.forEach((keypart, partindex) => {
            const fieldname_index = keypart.split('[')
            const fieldname = fieldname_index[0]
            const index =
                fieldname_index.length === 1
                    ? null
                    : parseInt(fieldname_index[1].replace(']', ''))
            if (partindex === 0) {
                if (recordfield.name !== fieldname) {
                    console.log('RecordData::setClassField RECORD FIELD ERROR')
                }
                if (partindex === lastpartindex) {
                    this.localized_fields.set(fieldname, value)
                    keyfound = true
                } else {
                    datapointer = this.localized_fields.get(fieldname)
                    if (datapointer === null || typeof datapointer === 'undefined') {
                        this.localized_fields.set(fieldname, new Map())
                        datapointer = this.localized_fields.get(fieldname)
                    }
                }
            } else {
                if (partindex === lastpartindex) {
                    datapointer.set(fieldname, value)
                    keyfound = true
                } else {
                    const trydatapointer = datapointer.get(fieldname)
                    if (
                        trydatapointer === null ||
                        typeof trydatapointer === 'undefined'
                    ) {
                        datapointer.set(fieldname, new Map())
                        datapointer = datapointer.get(fieldname)
                    } else {
                        datapointer = trydatapointer
                    }
                }
            }
            if (index !== null) {
                if (datapointer.length && index < datapointer.length) {
                    if (partindex === lastpartindex) {
                        datapointer[index] = value
                        keyfound = true
                    } else {
                        datapointer = datapointer[index]
                    }
                } else {
                    console.log('RecordData::setClassField RECORD FIELD INDEX ERROR')
                    keyfound = false
                }
            }
        })
        if (keyfound) {
            this._modified_fields.set(recordfield.gid, recordfield.name)
        }
    }

    setField = (field, value) => {
        if (['class', 'classlist'].includes(field.type)) {
            console.log("RecordData::setField ERROR - don't call on class fields!")
            return
        }
        if (!this._setField(field, value)) return false
        const title_field = this.title_field
        if (title_field && field.gid === title_field.gid) {
            this.title.set(this.language, value.toString())
        }
        return true
    }

    setFieldKey = (field, key) => {
        if (['class', 'classlist'].includes(field.type)) {
            console.log("RecordData::setFieldKey ERROR - don't call on class fields!")
            return
        }
        if (!this._setFieldKey(field, key)) return false
        const title_field = this.title_field
        if (title_field && field.gid === title_field.gid) {
            this.title.set(
                this.language,
                this.fields.get(this.language).get(field.name).toString()
            )
        }
        return true
    }

    resetField = field => {
        // mark field as not modified
        this._modified_fields.delete(field.gid)
        this._modified_fields.delete(field.gid + '/key')
        // just in case something else changed
        this.commitIfModified().then(data => {
            const language = this._rootstore.view.environment.get('language')
            // if committed, we have fresh data too
            if (data === 'committed') return data
            // otherwise, nothing was committed
            // refetch to update current record to valid field values
            return this._rootstore
                ._fetch('/records/get', {
                    record: this.gid,
                    language,
                })
                .then(result => {
                    this._modified_fields = new Map()
                    return result
                })
            // .catch(error => {})
        })
    }

    _setField = (field, value) => {
        const previous_value = this.localized_fields.get(field.name)
        if (previous_value === value) return false
        if (test_empty(previous_value) && test_empty(value)) return false

        this.fields.get(this.language).set(field.name, value)
        this._modified_fields.set(field.gid, field.name)
        return true
    }

    _setFieldKey = (field, key) => {
        const previous_key = this.localized_fields.get(field.name + '/key')
        if (previous_key === key) return false
        if (test_empty(previous_key) && test_empty(key)) return false

        this.fields.get(this.language).set(field.name + '/key', key)
        this.fields.get(this.language).set(field.name, key)
        var value = key
        if (field.type === 'textline') {
            for (const value_option of field['options'].get('values')) {
                if (key === value_option['key']) {
                    value = value_option[this.language]
                    break
                }
            }
            this.fields.get(this.language).set(field.name, value)
        } else if (field.type === 'textlist') {
            value = []
            for (var keyline of key) {
                for (const value_option of field['options'].get('values')) {
                    if (keyline === value_option['key']) {
                        value.push(value_option[this.language])
                        break
                    }
                }
            }
            this.fields.get(this.language).get(field.name).replace(value)
        }
        this._modified_fields.set(field.gid, field.name)
        this._modified_fields.set(field.gid + '/key', field.name + '/key')
        return true
    }

    commitIfModified = () => {
        // send gid + modified fields to server backend
        if (this._modified_fields.size) {
            let fields = {}
            fields[this.language] = {}
            for (const [gid, fieldname] of this._modified_fields.entries()) {
                fields[this.language][gid] = this.fields
                    .get(this.language)
                    .get(fieldname)
            }
            // basically fire & forget updates
            return this._rootstore
                ._fetch('/records/update', {
                    record: this.gid,
                    language: this.language,
                    fields: objectFrom(fields),
                })
                .then(result => {
                    this._modified_fields = new Map()
                    return result
                })
            // .catch(error => {})
        } else {
            return Promise.resolve(false)
        }
    }

    setChannels = (channelkeys, value) => {
        const channelvalue = value ? true : false
        channelkeys.forEach(key => {
            if (channelvalue && !this.channels.includes(key)) {
                this.channels.push(key)
            } else if (!channelvalue && this.channels.includes(key)) {
                this.channels.splice(this.channels.indexOf(key), 1)
            }
        })
        // basically fire & forget updates
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/channels/set', {
            record: this.gid,
            language,
            channels: channelkeys,
            enabled: channelvalue,
        })
        // .catch(error => {})
    }

    copyChannelsFromParent = channelkeys => {
        // a reload on succes is the best way to update the complete worksheet
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore
            ._fetch('/records/channels/copy_from_parent', {
                record: this.gid,
                language,
                channels: channelkeys,
            })
            .then(result => {
                this._rootstore.view.pimworksheet.refetch()
                return result
            })
        // .catch(error => {})
    }

    copyChannelsDown = channelkeys => {
        // a reload on succes is the best way to update the complete worksheet
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore
            ._fetch('/records/channels/copy_down', {
                record: this.gid,
                language,
                channels: channelkeys,
            })
            .then(result => {
                this._rootstore.view.pimworksheet.refetch()
                return result
            })
        // .catch(error => {})
    }

    copyChannelsUp = channelkeys => {
        // a reload on succes is the best way to update the complete worksheet
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore
            ._fetch('/records/channels/copy_up', {
                record: this.gid,
                language,
                channels: channelkeys,
            })
            .then(result => {
                this._rootstore.view.pimworksheet.refetch()
                return result
            })
        // .catch(error => {})
    }

    delete = () => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore
            ._fetch('/records/delete', {
                record: this.gid,
                language,
            })
            .then(result => {
                this._rootstore.view.pimworksheet.refetch()
                return result
            })
        // .catch(error => {})
    }

    undelete = () => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore
            ._fetch('/records/undelete', {
                record: this.gid,
                language,
            })
            .then(result => {
                this._rootstore.view.pimworksheet.refetch()
                return result
            })
        // .catch(error => {})
    }

    permanentDelete = () => {
        const language = this._rootstore.view.environment.get('language')
        this._rootstore.view.pimpinboard.remove(this.gid)
        return this._rootstore._fetch('/records/permanent_delete', {
            record: this.gid,
            language,
        }) // no refetch
        // .catch(error => {})
    }

    unlink = () => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore
            ._fetch('/records/unlink', {
                record: this.gid,
                language,
            })
            .then(result => {
                // this._rootstore.view.pimworksheet.refetch()
                return result
            })
    }

    addDefinitionField = (field_gid, before_field_gid) => {
        const language = this._rootstore.view.environment.get('language')
        let data = {
            record: this.gid,
            field: field_gid,
            language,
        }
        if (before_field_gid) {
            data['before_field'] = before_field_gid
        }
        return this._rootstore._fetch('/records/definition/fields/add', data)
    }

    moveDefinitionField = (field_gid, before_field_gid) => {
        const language = this._rootstore.view.environment.get('language')
        let data = {
            record: this.gid,
            field: field_gid,
            language,
        }
        if (before_field_gid) {
            data['before_field'] = before_field_gid
        }
        return this._rootstore._fetch('/records/definition/fields/move', data)
    }

    removeDefinitionField = field_gid => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/fields/remove', {
            record: this.gid,
            field: field_gid,
            language,
        })
    }

    addDefinitionClass = (class_gid, before_class_gid) => {
        const language = this._rootstore.view.environment.get('language')
        let data = {
            record: this.gid,
            class: class_gid,
            language,
        }
        if (before_class_gid) {
            data['before_class'] = before_class_gid
        }
        return this._rootstore._fetch('/records/definition/classes/add', data)
    }

    moveDefinitionClass = (class_gid, before_class_gid) => {
        const language = this._rootstore.view.environment.get('language')
        let data = {
            record: this.gid,
            class: class_gid,
            language,
        }
        if (before_class_gid) {
            data['before_class'] = before_class_gid
        }
        return this._rootstore._fetch('/records/definition/classes/move', data)
    }

    removeDefinitionClass = class_gid => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/classes/remove', {
            record: this.gid,
            class: class_gid,
            language,
        })
    }

    addDefinitionFieldlistField = (fieldlist_key, field_gid, before_field_gid) => {
        const language = this._rootstore.view.environment.get('language')
        let data = {
            record: this.gid,
            fieldlist: fieldlist_key,
            field: field_gid,
            language,
        }
        if (before_field_gid) {
            data['before_field'] = before_field_gid
        }
        return this._rootstore._fetch('/records/definition/fieldlists/fields/add', data)
    }

    moveDefinitionFieldlistField = (fieldlist_key, field_gid, before_field_gid) => {
        const language = this._rootstore.view.environment.get('language')
        let data = {
            record: this.gid,
            fieldlist: fieldlist_key,
            field: field_gid,
            language,
        }
        if (before_field_gid) {
            data['before_field'] = before_field_gid
        }
        return this._rootstore._fetch(
            '/records/definition/fieldlists/fields/move',
            data
        )
    }

    removeDefinitionFieldlistField = (fieldlist_key, field_gid) => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/fieldlists/fields/remove', {
            record: this.gid,
            fieldlist: fieldlist_key,
            field: field_gid,
            language,
        })
    }

    setDefinition = definition_gid => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/set', {
            record: this.gid,
            definition: definition_gid,
            language,
        })
    }

    revertDefinition = () => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/revert', {
            record: this.gid,
            language,
        })
    }

    copyDefinitionToSiblings = () => {
        return this._rootstore._fetch('/records/definition/copy_to_siblings', {
            record: this.gid,
        })
    }

    // supported properties: name, base_definition, fields, classes, fieldlists
    cloneExtendedDefinition = (extendedDefinitionData = {}) => {
        const language = this._rootstore.view.environment.get('language')
        const payload = {
            record: this.gid,
            language,
            ...Object.fromEntries(
                Object.entries(extendedDefinitionData).filter(
                    ([key, value]) =>
                        [
                            'name',
                            'base_definition',
                            'fields',
                            'classes',
                            'fieldlists',
                        ].includes(key) && value !== undefined
                )
            ),
        }
        return this._rootstore._fetch('/records/definition/clone', payload)
    }

    // supported properties: name, base_definition, fields, classes, fieldlists
    updateExtendedDefinition = (extendedDefinitionData = {}) => {
        const language = this._rootstore.view.environment.get('language')
        const payload = {
            record: this.gid,
            language,
            ...Object.fromEntries(
                Object.entries(extendedDefinitionData).filter(
                    ([key, value]) =>
                        [
                            'name',
                            'base_definition',
                            'fields',
                            'classes',
                            'fieldlists',
                        ].includes(key) && value !== undefined
                )
            ),
        }
        return this._rootstore._fetch('/records/definition/update', payload)
    }

    transferExtendedDefinitionTo = target_definition_gid => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/transfer_to', {
            record: this.gid,
            target_definition: target_definition_gid,
            language,
        })
    }

    transferExtendedDefinitionFrom = source_definition_gid => {
        const language = this._rootstore.view.environment.get('language')
        return this._rootstore._fetch('/records/definition/transfer_from', {
            record: this.gid,
            source_definition: source_definition_gid,
            language,
        })
    }
}
